import {
  ActionFunction,
  AnyActorRef,
  assign,
  fromCallback,
  NonReducibleUnknown,
  sendTo,
  setup,
} from 'xstate';
import { claimsMachine } from './claims/claims.machine';
import { certificateUploadMachine } from './insurance/certificateUpload.machine';
import { insuranceMachine } from './insurance/insurance.machine';
import { Context, Events, OnInsuranceParseCompletedEvent, OnSetActiveTabEvent } from './types';
import { MALPRACTICE_TABS } from './utils';

const CLAIMS_TAB_INDEX = MALPRACTICE_TABS.findIndex((tab) => tab.state === '$_CLAIMS_TAB');

const INSURANCE_RELOADER = 'insuranceReloader';

/**
 * Reloads the insurance table after a certificate parse saved a policy. ON_LOAD is dropped unless
 * the table is idle, and a reload must not replace a form the user is filling in, so a completion
 * that arrives while the form is open or a load is in flight is remembered and the reload is sent
 * as soon as the table is back in `$_VIEW`. Any number of completions in that time make one reload.
 * As an invoked actor it watches the table for exactly as long as the malpractice actor lives.
 */
const insuranceReloader = fromCallback<
  OnInsuranceParseCompletedEvent,
  { insurancesRef: AnyActorRef }
>(({ input: { insurancesRef }, receive }) => {
  let reloadPending = false;
  const isIdle = () => insurancesRef.getSnapshot().matches('$_VIEW');
  const reload = () => {
    reloadPending = false;
    insurancesRef.send({ type: 'ON_LOAD' });
  };

  const subscription = insurancesRef.subscribe(() => {
    if (reloadPending && isIdle()) reload();
  });
  receive(() => {
    if (isIdle()) reload();
    else reloadPending = true;
  });

  return () => subscription.unsubscribe();
});

const createTabState = (target: string, actor: keyof Context) => {
  return {
    entry: [
      ({ context }: { context: Context }) => {
        (context[actor] as AnyActorRef).send({ type: 'ON_LOAD' });
      },
    ],
    on: {
      ON_SET_ACTIVE_TAB: {
        target,
        // TODO: MCV-272 update with better typing
        actions: ['setActiveTab'] as unknown as ActionFunction<
          Context,
          Events,
          Events,
          NonReducibleUnknown,
          never,
          never,
          never,
          never,
          never
        >,
        guard: ({ event }: { event: OnSetActiveTabEvent; context: Context }) =>
          MALPRACTICE_TABS[event.index].state === target,
      },
    },
  };
};

export const malpracticeMachine = setup({
  actors: {
    claimsRef: claimsMachine,
    insurancesRef: insuranceMachine,
    certificateUploadRef: certificateUploadMachine,
    insuranceReloader,
  },
  actions: {
    setActiveTab: assign({
      activeTab: ({ event }) => (event as OnSetActiveTabEvent).index,
    }),
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
}).createMachine({
  context: ({ spawn }) => ({
    activeTab: 0,
    claimsRef: spawn('claimsRef', {
      id: 'claimsState',
      input: { data: [] },
    }),
    insurancesRef: spawn('insurancesRef', {
      id: 'insurancesState',
      input: { data: [] },
    }),
    certificateUploadRef: spawn('certificateUploadRef', { id: 'insuranceCertificateUpload' }),
  }),
  initial: '$_IDLE',
  invoke: {
    id: INSURANCE_RELOADER,
    src: 'insuranceReloader',
    input: ({ context }) => ({ insurancesRef: context.insurancesRef }),
  },
  on: {
    ON_INSURANCE_PARSE_COMPLETED: {
      actions: sendTo(INSURANCE_RELOADER, ({ event }) => event),
    },
    ON_LOAD: [
      {
        target: '.$_INSURANCES_TAB',
        guard: ({ context }) => context.activeTab !== CLAIMS_TAB_INDEX,
      },
      {
        target: '.$_CLAIMS_TAB',
        guard: ({ context }) => context.activeTab === CLAIMS_TAB_INDEX,
      },
    ],
  },
  states: {
    $_IDLE: {},
    $_CLAIMS_TAB: createTabState('$_INSURANCES_TAB', 'claimsRef'),
    $_INSURANCES_TAB: createTabState('$_CLAIMS_TAB', 'insurancesRef'),
  },
});
